import { useEffect } from 'react'

import { useCart, useCartDialog } from '../../app/_providers/CartProvider.client'
import { shortenShopifyId } from '../../app/_utils/shortenShopifyId'
import { MakeCartItemProps, useMakeCartItem } from '../../brunswick/containers/cart/utils'
import { isBrowser } from '../utils'

const NEXT_PUBLIC_SHOPIFY_SHOP_NAME = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_NAME
const NEXT_PUBLIC_SHOPIFY_SHOP_DOMAIN = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_DOMAIN
const shopifyShopUrl = `${NEXT_PUBLIC_SHOPIFY_SHOP_NAME ?? 'wearfigs-staging'}.${
  NEXT_PUBLIC_SHOPIFY_SHOP_DOMAIN ?? 'myshopify.com'
}`

type UseRiseAiScriptProps = {
  product: MakeCartItemProps['product']
  variant: undefined | null | MakeCartItemProps['variant']
  variantGroup: MakeCartItemProps['variantGroup']
}

export const useRiseAiScript = ({ variant, variantGroup, product }: UseRiseAiScriptProps) => {
  const { addItems } = useCart()
  const { open: openCartDialog } = useCartDialog()
  const makeCartItem = useMakeCartItem()

  // DO NOT store things on the server. Server handles many users. Only store things on the browser.
  if (isBrowser() && variant) {
    globalThis.FigsRise = globalThis.FigsRise ?? {
      adding: false,
    }
    // Store stuff in a pointer based storage in a non-reactive place since Rise AI script cannot be altered after initial setup
    globalThis.FigsRise.onRiseGiftAdded = riseData => {
      addItems([
        makeCartItem({
          product,
          variant,
          variantGroup,
          options: {
            attributedPortal: undefined,
            properties: riseData.gift,
          },
        }),
      ])
      openCartDialog()
    }
  }

  useEffect(() => {
    // See: https://help.rise.ai/en/articles/4858938-using-rise-with-a-headless-store
    if (globalThis?.Rise?.product?.id || !variant) return

    globalThis.Rise = {
      full_product: { available: true },
      is_floating_cart_theme: true,
      is_product_page: true,
      product: { id: parseInt(shortenShopifyId(variant.shopifyProductId)) },
      using_add_to_cart_flow: true,
      onGiftAdded: (
        arg0: Parameters<NonNullable<NonNullable<typeof globalThis.FigsRise>['onRiseGiftAdded']>>[0]
      ) => {
        // Since clientside nav can double this up. We have to block it for all but one add.
        const currentRisePopup = document.getElementsByClassName('Rise-popup open')[0]
        if (
          currentRisePopup &&
          globalThis.FigsRise?.onRiseGiftAdded &&
          globalThis.FigsRise.adding === false
        ) {
          globalThis.FigsRise.adding = true

          // Do not allow another digital gift card add until the pop up has finished this addition.
          // Consider it finished with the pop up has closed.
          new MutationObserver((mutationsList, observer) => {
            for (const mutation of mutationsList) {
              if (
                globalThis.FigsRise?.adding &&
                mutation.type === 'attributes' &&
                mutation.attributeName === 'class' &&
                !currentRisePopup.className.includes('show')
              ) {
                globalThis.FigsRise.adding = false
                observer.disconnect()
              }
            }
          }).observe(currentRisePopup, {
            attributes: true,
            attributeFilter: ['class'],
            attributeOldValue: false,
          })

          globalThis.FigsRise.onRiseGiftAdded(arg0)
        }
      },
    }
  }, [variant])

  useEffect(() => {
    if (globalThis.document) {
      const targetScriptElement = globalThis.document.getElementById('@figs:rise')
      const addToCartButton = globalThis.document.querySelector('.Rise-add-to-cart-button')
      const addToCartButtonInitializedAttr = addToCartButton?.getAttribute('data-initialized')
      if (targetScriptElement && addToCartButtonInitializedAttr) {
        return
      }

      // Unfortunately, client side nav will double set the RiseAI code. We can't get around that because we need it to attach to the new button.

      new Promise((resolve, reject) => {
        const script = globalThis.document.createElement('script')
        script.id = '@figs:rise'
        script.onload = resolve
        script.onerror = reject
        script.async = true
        script.src = `https://str.rise-ai.com/?shop=${shopifyShopUrl}`
        const targetElement = globalThis.document.getElementById('@figs:rise')
        if (targetElement && targetElement.outerHTML) {
          targetElement.outerHTML = ''
        }
        globalThis.document.body.appendChild(script)
        addToCartButton?.setAttribute('data-initialized', 'true')
      })
    }
  }, [])
}
